import React, { useState } from 'react';
import '../CRMProfileScreen.css';

const BankingVerificationModal = ({ 
    isOpen, 
    onClose, 
    analysisData,
    onAccept,
    onDecline
}) => {
    const [isAccepting, setIsAccepting] = useState(false);
    const [isDeclining, setIsDeclining] = useState(false);
    
    if (!isOpen || !analysisData) return null;

    const handleAccept = async () => {
        setIsAccepting(true);
        try {
            await onAccept();
        } finally {
            setIsAccepting(false);
        }
    };

    const handleDecline = async () => {
        setIsDeclining(true);
        try {
            await onDecline();
        } finally {
            setIsDeclining(false);
        }
    };

    const getStatusColor = (status) => {
        return status === 'VERIFIED' ? '#10b981' : '#ef4444';
    };

    const getConfidenceColor = (score) => {
        if (score >= 0.8) return '#10b981';
        if (score >= 0.6) return '#f59e0b';
        return '#ef4444';
    };

    const formatConfidenceScore = (score) => {
        return `${Math.round(score * 100)}%`;
    };

    return (
        <div className="modal-overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
        }}>
            <div className="modal-content" style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                width: '90%',
                maxWidth: '800px',
                maxHeight: '90%',
                overflow: 'auto',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}>
                {/* Modal Header */}
                <div style={{
                    padding: '1.5rem',
                    borderBottom: '1px solid #e5e7eb',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <h2 style={{
                        margin: 0,
                        fontSize: '1.5rem',
                        fontWeight: '700',
                        color: '#111827'
                    }}>
                        AI Banking Verification Results
                    </h2>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '1.5rem',
                            cursor: 'pointer',
                            color: '#6b7280',
                            padding: '0.25rem'
                        }}
                    >
                        ×
                    </button>
                </div>

                {/* Modal Body */}
                <div style={{ padding: '1.5rem' }}>
                    {/* Verification Status */}
                    <div style={{
                        marginBottom: '2rem',
                        padding: '1rem',
                        backgroundColor: analysisData.verification_status === 'VERIFIED' ? '#f0fdf4' : '#fef2f2',
                        border: `1px solid ${analysisData.verification_status === 'VERIFIED' ? '#bbf7d0' : '#fecaca'}`,
                        borderRadius: '8px'
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '0.5rem'
                        }}>
                            <h3 style={{
                                margin: 0,
                                fontSize: '1.25rem',
                                fontWeight: '600',
                                color: getStatusColor(analysisData.verification_status)
                            }}>
                                {analysisData.verification_status === 'VERIFIED' ? 'VERIFIED' : 'UNVERIFIED'}
                            </h3>
                            <div style={{
                                fontSize: '1rem',
                                fontWeight: '600',
                                color: getConfidenceColor(analysisData.confidence_score)
                            }}>
                                Confidence: {formatConfidenceScore(analysisData.confidence_score)}
                            </div>
                        </div>
                        <p style={{
                            margin: 0,
                            fontSize: '0.875rem',
                            color: '#374151'
                        }}>
                            {analysisData.verification_status === 'VERIFIED' 
                                ? 'Banking details have been successfully verified through AI analysis.'
                                : 'Banking details could not be verified. Manual review recommended.'}
                        </p>
                    </div>

                    {/* Extracted Banking Data */}
                    {analysisData.extracted_banking_data && Object.keys(analysisData.extracted_banking_data).length > 0 && (
                        <div style={{ marginBottom: '2rem' }}>
                            <h4 style={{
                                margin: '0 0 1rem 0',
                                fontSize: '1.125rem',
                                fontWeight: '600',
                                color: '#111827'
                            }}>
                                Extracted Banking Data
                            </h4>
                            <div style={{
                                backgroundColor: '#f9fafb',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                padding: '1rem'
                            }}>
                                {analysisData.extracted_banking_data.account_holders?.length > 0 && (
                                    <div style={{ marginBottom: '0.75rem' }}>
                                        <strong>Account Holders:</strong> {analysisData.extracted_banking_data.account_holders.join(', ')}
                                    </div>
                                )}
                                {analysisData.extracted_banking_data.bank_names?.length > 0 && (
                                    <div style={{ marginBottom: '0.75rem' }}>
                                        <strong>Bank Names:</strong> {analysisData.extracted_banking_data.bank_names.join(', ')}
                                    </div>
                                )}
                                {analysisData.extracted_banking_data.iban_codes?.length > 0 && (
                                    <div style={{ marginBottom: '0.75rem' }}>
                                        <strong>IBAN Codes:</strong> {analysisData.extracted_banking_data.iban_codes.join(', ')}
                                    </div>
                                )}
                                {analysisData.extracted_banking_data.swift_codes?.length > 0 && (
                                    <div style={{ marginBottom: '0.75rem' }}>
                                        <strong>SWIFT Codes:</strong> {analysisData.extracted_banking_data.swift_codes.join(', ')}
                                    </div>
                                )}
                                {analysisData.extracted_banking_data.account_numbers?.length > 0 && (
                                    <div style={{ marginBottom: '0.75rem' }}>
                                        <strong>Account Numbers:</strong> {analysisData.extracted_banking_data.account_numbers.join(', ')}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Verification Reasoning */}
                    <div style={{ marginBottom: '2rem' }}>
                        <h4 style={{
                            margin: '0 0 1rem 0',
                            fontSize: '1.125rem',
                            fontWeight: '600',
                            color: '#111827'
                        }}>
                            Verification Reasoning
                        </h4>
                        <div style={{
                            backgroundColor: '#f9fafb',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            padding: '1rem',
                            fontSize: '0.875rem',
                            lineHeight: '1.5',
                            color: '#374151'
                        }}>
                            {analysisData.verification_reasoning || 'No reasoning provided.'}
                        </div>
                    </div>

                    {/* Key Evidence */}
                    {analysisData.processing_metadata?.key_evidence && (
                        <div style={{ marginBottom: '2rem' }}>
                            <h4 style={{
                                margin: '0 0 1rem 0',
                                fontSize: '1.125rem',
                                fontWeight: '600',
                                color: '#111827'
                            }}>
                                Key Evidence
                            </h4>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '1rem'
                            }}>
                                {/* Positive Indicators */}
                                {analysisData.processing_metadata.key_evidence.positive_indicators?.length > 0 && (
                                    <div style={{
                                        backgroundColor: '#f0fdf4',
                                        border: '1px solid #bbf7d0',
                                        borderRadius: '8px',
                                        padding: '1rem'
                                    }}>
                                        <h5 style={{
                                            margin: '0 0 0.5rem 0',
                                            fontSize: '1rem',
                                            fontWeight: '600',
                                            color: '#059669'
                                        }}>
                                            [+] Positive Indicators
                                        </h5>
                                        <ul style={{ margin: 0, paddingLeft: '1.5rem', fontSize: '0.875rem' }}>
                                            {analysisData.processing_metadata.key_evidence.positive_indicators.map((indicator, index) => (
                                                <li key={index} style={{ marginBottom: '0.25rem' }}>{indicator}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Negative Indicators */}
                                {analysisData.processing_metadata.key_evidence.negative_indicators?.length > 0 && (
                                    <div style={{
                                        backgroundColor: '#fef2f2',
                                        border: '1px solid #fecaca',
                                        borderRadius: '8px',
                                        padding: '1rem'
                                    }}>
                                        <h5 style={{
                                            margin: '0 0 0.5rem 0',
                                            fontSize: '1rem',
                                            fontWeight: '600',
                                            color: '#dc2626'
                                        }}>
                                            [-] Negative Indicators
                                        </h5>
                                        <ul style={{ margin: 0, paddingLeft: '1.5rem', fontSize: '0.875rem' }}>
                                            {analysisData.processing_metadata.key_evidence.negative_indicators.map((indicator, index) => (
                                                <li key={index} style={{ marginBottom: '0.25rem' }}>{indicator}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Processing Metadata */}
                    <div style={{ marginBottom: '2rem' }}>
                        <h4 style={{
                            margin: '0 0 1rem 0',
                            fontSize: '1.125rem',
                            fontWeight: '600',
                            color: '#111827'
                        }}>
                            Processing Details
                        </h4>
                        <div style={{
                            backgroundColor: '#f9fafb',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            padding: '1rem'
                        }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', fontSize: '0.875rem' }}>
                                <div>
                                    <strong>Documents Processed:</strong> {analysisData.processing_metadata?.documents_processed || 0}
                                </div>
                                <div>
                                    <strong>Documents Converted:</strong> {analysisData.processing_metadata?.documents_converted || 0}
                                </div>
                                <div>
                                    <strong>External Validation:</strong> <span style={{ color: analysisData.processing_metadata?.external_validation_completed ? '#10b981' : '#ef4444', fontWeight: '600' }}>{analysisData.processing_metadata?.external_validation_completed ? 'Completed' : 'Failed'}</span>
                                </div>
                                <div>
                                    <strong>AI Decision:</strong> <span style={{ color: analysisData.processing_metadata?.decision_successful ? '#10b981' : '#ef4444', fontWeight: '600' }}>{analysisData.processing_metadata?.decision_successful ? 'Successful' : 'Failed'}</span>
                                </div>
                                <div>
                                    <strong>Analysis Time:</strong> {new Date(analysisData.timestamp).toLocaleString()}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Modal Footer */}
                <div style={{
                    padding: '1.5rem',
                    borderTop: '1px solid #e5e7eb',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '1rem'
                }}>
                    <button
                        onClick={handleDecline}
                        disabled={isAccepting || isDeclining}
                        style={{
                            backgroundColor: '#6b7280',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '0.75rem 1.5rem',
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            cursor: (isAccepting || isDeclining) ? 'not-allowed' : 'pointer',
                            opacity: (isAccepting || isDeclining) ? 0.6 : 1,
                            transition: 'all 0.2s',
                            minWidth: '120px'
                        }}
                    >
                        {isDeclining ? 'Declining...' : 'Decline'}
                    </button>
                    <button
                        onClick={handleAccept}
                        disabled={isAccepting}
                        style={{
                            backgroundColor: analysisData.verification_status === 'VERIFIED' ? '#10b981' : '#4a6b25',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '0.75rem 1.5rem',
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            cursor: isAccepting ? 'not-allowed' : 'pointer',
                            opacity: isAccepting ? 0.6 : 1,
                            transition: 'all 0.2s',
                            minWidth: '120px'
                        }}
                    >
                        {isAccepting ? 'Accepting...' : (
                            analysisData.verification_status === 'VERIFIED' 
                                ? 'Accept & Update Checkbox' 
                                : 'Accept Result'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BankingVerificationModal; 